import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Popper,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { FixedSizeList as VirtualizedList } from 'react-window';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import theme from 'app/theme';
import { useTranslation } from 'react-i18next';
import { sentryMaskIf } from 'sentryReplayPrivacy';
import { debounce } from './utils';

// Safe highlighting utility function
// This function safely highlights matching text without using dangerouslySetInnerHTML,
// preventing XSS vulnerabilities. It splits the text by the search term and wraps
// matching parts in styled mark elements.
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm.trim()) {
    return text;
  }

  // Escape special regex characters to prevent regex injection
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escapedSearchTerm})`, 'gi'));

  return parts.map((part, index) => {
    if (part.toLowerCase() === searchTerm.toLowerCase()) {
      return (
        <mark
          key={index}
          style={{
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            padding: '0 2px',
            borderRadius: '2px',
            fontWeight: 'bold',
          }}
        >
          {part}
        </mark>
      );
    }
    return part;
  });
};

// Raw option field names used for search when display label omits them (e.g. description)
const SEARCHABLE_RAW_FIELDS = ['name', 'code', 'description', 'fullName'];

// Native throttle function to replace lodash throttle
const throttle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): { throttled: T; cleanup: () => void } => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastExecTime = 0;
  const { leading = true, trailing = true } = options;

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const throttled = ((...args: any[]) => {
    const now = Date.now();

    // Check if enough time has passed since last execution
    const timeSinceLastExec = now - lastExecTime;
    const shouldExecuteImmediately = timeSinceLastExec >= delay;

    if (lastExecTime === 0 && leading) {
      // First call with leading enabled
      func(...args);
      lastExecTime = now;
    } else if (shouldExecuteImmediately) {
      // Enough time has passed, execute immediately
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      func(...args);
      lastExecTime = now;
    } else if (trailing) {
      // Schedule execution for later
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      const remainingDelay = delay - timeSinceLastExec;
      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
        timeoutId = null;
      }, remainingDelay);
    }
  }) as T;

  return { throttled, cleanup };
};

// Robust deep equality function to replace JSON.stringify comparison
// This function handles circular references, functions, undefined, symbols, and property order correctly
const deepEqual = (a: unknown, b: unknown, visited = new WeakSet(), comparingPairs = new Map()): boolean => {
  // Handle primitive types and null/undefined
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;

  // Handle functions
  if (typeof a === 'function') {
    if (typeof b !== 'function') return false;
    return a.toString() === b.toString();
  }

  // Handle symbols
  if (typeof a === 'symbol') {
    return a === b;
  }

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle regular expressions
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i], visited, comparingPairs)) return false;
    }
    return true;
  }

  // Handle objects (including plain objects, class instances, etc.)
  if (typeof a === 'object' && typeof b === 'object') {
    // Create a unique key for this pair of objects
    const pairKey = `${a}-${b}`;

    // Check if this pair is already being compared (circular reference)
    if (comparingPairs.has(pairKey)) {
      return true; // Break the cycle by assuming equality
    }

    // Add this pair to the comparing map before continuing
    comparingPairs.set(pairKey, true);

    // Also track individual objects in visited set for additional safety
    if (visited.has(a as object) && visited.has(b as object)) {
      comparingPairs.delete(pairKey);
      return true;
    }

    visited.add(a as object);
    visited.add(b as object);

    const keysA = Object.keys(a as object);
    const keysB = Object.keys(b as object);

    if (keysA.length !== keysB.length) {
      comparingPairs.delete(pairKey);
      return false;
    }

    // Check if all keys in A exist in B
    for (const key of keysA) {
      if (!(key in (b as object))) {
        comparingPairs.delete(pairKey);
        return false;
      }
    }

    // Check if all keys in B exist in A
    for (const key of keysB) {
      if (!(key in (a as object))) {
        comparingPairs.delete(pairKey);
        return false;
      }
    }

    // Compare all properties
    for (const key of keysA) {
      if (!deepEqual((a as any)[key], (b as any)[key], visited, comparingPairs)) {
        comparingPairs.delete(pairKey);
        return false;
      }
    }

    // Clean up the pair from the map after successful comparison
    comparingPairs.delete(pairKey);
    return true;
  }

  return false;
};

const COMMON_STYLE = {
  width: '100%',
  backgroundColor: theme.palette.primary.lighter,
  paddingLeft: '10px',
  borderRadius: '8px',
};

export interface TableDropdownOption<T = unknown> {
  id: string | number;
  label: string;
  value: T;
  disabled?: boolean;
}

export interface TableDropdownProps<T = unknown> {
  // Core props
  options: TableDropdownOption<T>[];
  value?: T | null;
  onSelect: (option: TableDropdownOption<T> | null) => void;

  // Display props
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';

  // Search props
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (searchTerm: string) => void;
  searchDebounceMs?: number;

  // Performance props
  virtualized?: boolean;
  maxHeight?: number;
  itemHeight?: number;

  // Customization props
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  itemClassName?: string;

  // Data attributes for styling
  'data-dropdown-type'?: string;
  'data-dropdown-type-input'?: string;

  // Render props
  renderOption?: (option: TableDropdownOption<T>, isSelected: boolean) => React.ReactNode;
  renderInput?: (props: Record<string, unknown>) => React.ReactNode;
  renderEmpty?: () => React.ReactNode;

  // Callbacks
  onOpen?: () => void;
  onClose?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;

  // Async loading
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean; // External loading state for load more operations

  // Keyboard navigation
  enableKeyboardNavigation?: boolean;

  // Performance optimization
  optionsVersion?: string | number; // Optional version/hash to track options changes

  /**
   * SEE-1834: the option labels are PII (ERP employee names). Masks the selected value, its
   * tooltip and the options popper (search text included) in session replays.
   */
  maskOptionLabels?: boolean;
}

export interface TableDropdownRef {
  open: () => void;
  close: () => void;
  focus: () => void;
  blur: () => void;
  clear: () => void;
}

const TableDropdown = forwardRef<TableDropdownRef, TableDropdownProps>(
  (
    {
      options = [],
      value,
      onSelect,
      placeholder,
      label,
      disabled = false,
      loading = false,
      error = false,
      helperText,
      size = 'medium',
      searchable = true,
      searchPlaceholder,
      onSearch,
      searchDebounceMs = 300,
      virtualized = true,
      maxHeight = 300,
      itemHeight = 48,
      className,
      inputClassName,
      dropdownClassName,
      itemClassName,
      'data-dropdown-type': dataDropdownType,
      'data-dropdown-type-input': dataDropdownTypeInput,
      renderOption,
      renderInput,
      renderEmpty,
      onOpen,
      onClose,
      onFocus,
      onBlur,
      hasMore = false,
      onLoadMore,
      isLoadingMore = false, // Use external loading state
      enableKeyboardNavigation = true,
      maskOptionLabels = false,
    },
    ref
  ) => {
    const { t } = useTranslation();

    // Default values using i18n
    const defaultPlaceholder = placeholder || t('tableDropdown.selectOption', 'Select an option');
    const defaultSearchPlaceholder =
      searchPlaceholder || t('tableDropdown.typeToSearch', 'Type to search...');
    const defaultUnassignedText = t('tableDropdown.unassigned', 'Unassigned');

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [inputValue, setInputValue] = useState('');

    const resetSearchState = useCallback(() => {
      setSearchTerm('');
      setHighlightedIndex(-1);
    }, []);

    const anchorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<VirtualizedList>(null);
    const searchInputRef = useRef<HTMLDivElement>(null);
    const dropdownContentRef = useRef<HTMLDivElement>(null);
    const isLoadingMoreRef = useRef(false);

    // Update ref whenever isLoadingMore changes
    useEffect(() => {
      isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    // Find selected option
    const selectedOption = useMemo(() => {
      // Handle null/undefined values properly
      if (value === null || value === undefined) {
        return null;
      }

      // First try strict equality for performance
      const strictMatch = options.find((option) => option.value === value);
      if (strictMatch) return strictMatch;

      // If no strict match, try to find by ID or other unique properties
      if (value && typeof value === 'object') {
        // Try to find by ID if both objects have an id property
        if ('id' in value) {
          const idMatch = options.find((option) => {
            if (!option.value || typeof option.value !== 'object') return false;
            if (!('id' in option.value)) return false;

            // Now TypeScript knows both objects have id properties
            const valueWithId = value as { id: unknown };
            const optionValueWithId = option.value as { id: unknown };
            return optionValueWithId.id === valueWithId.id;
          });
          if (idMatch) return idMatch;
        }

        // Try to find by comparing all properties (fallback for objects without id)
        const deepMatch = options.find((option) => {
          if (!option.value || typeof option.value !== 'object') return false;
          return deepEqual(option.value, value);
        });
        if (deepMatch) return deepMatch;
      }

      return null;
    }, [options, value]);

    // Filter options based on search term (label/id and raw option fields so suffix/middle segments like description match)
    const filteredOptions = useMemo(() => {
      if (!searchTerm.trim()) return options;

      const searchLower = searchTerm.toLowerCase();
      const result = options.filter((option) => {
        const matchesLabel =
          option.label.toLowerCase().includes(searchLower) ||
          option.id.toString().toLowerCase().includes(searchLower);
        if (matchesLabel) return true;

        const raw = option.value;
        const matchesRaw =
          raw &&
          typeof raw === 'object' &&
          SEARCHABLE_RAW_FIELDS.some((field) => {
            const val = (raw as Record<string, unknown>)[field];
            return val != null && String(val).toLowerCase().includes(searchLower);
          });
        return !!matchesRaw;
      });
      return result;
    }, [options, searchTerm]);

    // Debounced search callback
    const debouncedSearch = useMemo(
      () =>
        debounce((term: string) => {
          if (onSearch) {
            onSearch(term);
          }
        }, searchDebounceMs),
      [onSearch, searchDebounceMs]
    );

    // Throttled load more callback to prevent rapid firing during scroll
    const throttledLoadMoreResult = useMemo(
      () =>
        throttle(
          () => {
            if (hasMore && onLoadMore && !isLoadingMore) {
              onLoadMore();
            }
          },
          1000, // Increased from 500ms to 1000ms for better throttling
          { leading: true, trailing: false } // Only leading, no trailing to prevent multiple calls
        ),
      [hasMore, onLoadMore, isLoadingMore]
    );

    const { throttled: throttledLoadMore, cleanup: throttledLoadMoreCleanup } = throttledLoadMoreResult;

    // Cleanup throttled function on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        throttledLoadMoreCleanup();
      };
    }, [throttledLoadMoreCleanup]);

    // Handle search input change
    const handleSearchChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = event.target.value;
        setSearchTerm(newSearchTerm);
        debouncedSearch(newSearchTerm);
        setHighlightedIndex(-1);
      },
      [debouncedSearch]
    );

    // Handle option selection
    const handleOptionSelect = useCallback(
      (option: TableDropdownOption) => {
        if (option.disabled) return;

        onSelect(option);
        setIsOpen(false);
        resetSearchState();
        setInputValue(option.label);
        onClose?.();
      },
      [onSelect, onClose, resetSearchState]
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (!enableKeyboardNavigation) return;

        // If dropdown is open, handle navigation keys
        if (isOpen) {
          // Allow navigation keys to be handled
          if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(event.key)) {
            switch (event.key) {
              case 'ArrowDown':
                event.preventDefault();
                setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
                break;
              case 'ArrowUp':
                event.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
              case 'Enter':
                event.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                  handleOptionSelect(filteredOptions[highlightedIndex]);
                }
                break;
              case 'Escape':
                event.preventDefault();
                setIsOpen(false);
                resetSearchState();
                onClose?.();
                if (inputRef.current) {
                  inputRef.current.focus();
                }
                break;
              case 'Tab':
                setIsOpen(false);
                resetSearchState();
                onClose?.();
                break;
            }
          } else {
            // For typing keys, redirect to search input if available
            const isSearchInput =
              event.currentTarget === searchInputRef.current ||
              searchInputRef.current?.contains(event.currentTarget as Node);

            if (!isSearchInput && searchable) {
              // Redirect typing to search input
              if (searchInputRef.current) {
                const inputElement = searchInputRef.current.querySelector('input');
                if (inputElement) {
                  inputElement.focus();
                  // Simulate the key press on the search input using a more compatible approach
                  const currentValue = inputElement.value;
                  const newValue = currentValue + event.key;

                  // Update the input value directly
                  inputElement.value = newValue;

                  // Create a compatible input event
                  const inputEvent = new Event('input', {
                    bubbles: true,
                    cancelable: true,
                  });

                  // Dispatch the event to trigger React's onChange handler
                  inputElement.dispatchEvent(inputEvent);
                }
              }
              event.preventDefault();
            }
          }
          return;
        }

        // If dropdown is closed, handle opening
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleToggle();
        }
      },
      [
        isOpen,
        filteredOptions,
        highlightedIndex,
        handleOptionSelect,
        enableKeyboardNavigation,
        searchable,
        onClose,
        resetSearchState,
      ]
    );

    // Handle dropdown open/close
    const handleToggle = useCallback(() => {
      if (disabled) return;

      if (isOpen) {
        setIsOpen(false);
        resetSearchState();
        onClose?.();
        onBlur?.();
      } else {
        setIsOpen(true);
        onOpen?.();
        onFocus?.();
      }
    }, [isOpen, disabled, onOpen, onClose, onFocus, onBlur, resetSearchState]);

    // Handle click outside - simplified since we now use a backdrop
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const anchorElement = anchorRef.current;
        const dropdownContentElement = dropdownContentRef.current;

        // Check if click is outside both the anchor element and dropdown content
        const isOutsideAnchor = !anchorElement || !anchorElement.contains(target);
        const isOutsideDropdown = !dropdownContentElement || !dropdownContentElement.contains(target);

        if (isOutsideAnchor && isOutsideDropdown) {
          setIsOpen(false);
          resetSearchState();
          onClose?.();
          onBlur?.();
        }
      };

      // Only add the event listener as a fallback for edge cases
      // The backdrop handles most outside click scenarios
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen, onClose, onBlur, resetSearchState]);

    // Update input value when selected option changes
    useEffect(() => {
      setInputValue(selectedOption?.label || '');
    }, [selectedOption]);

    // Focus search input when dropdown opens using useLayoutEffect
    useLayoutEffect(() => {
      if (isOpen && searchable) {
        // Simple focus strategy - only focus if search input is not already focused
        const focusSearchInput = () => {
          if (searchInputRef.current && document.contains(searchInputRef.current)) {
            const inputElement = searchInputRef.current.querySelector('input');
            if (inputElement && document.activeElement !== inputElement) {
              inputElement.focus();
              inputElement.select();
              return true;
            }
          }
          return false;
        };

        // Try to focus with a small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          focusSearchInput();
        }, 10);

        return () => clearTimeout(timer);
      }
    }, [isOpen, searchable]);

    // Scroll to highlighted item
    useEffect(() => {
      if (highlightedIndex >= 0 && listRef.current && virtualized) {
        listRef.current.scrollToItem(highlightedIndex, 'center');
      }
    }, [highlightedIndex, virtualized]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      clear: () => {
        onSelect(null);
        setInputValue('');
        setSearchTerm('');
      },
    }));

    // Render option item
    const renderOptionItem = useCallback(
      (option: TableDropdownOption, index: number) => {
        const isSelected = option.value === value;
        const isHighlighted = index === highlightedIndex;

        if (renderOption) {
          return renderOption(option, isSelected);
        }

        return (
          <ListItem
            key={option.id}
            component="li"
            role="option"
            aria-selected={isSelected}
            disabled={option.disabled}
            selected={isSelected}
            className={itemClassName}
            sx={{
              backgroundColor: isHighlighted ? 'action.hover' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              },
              height: itemHeight,
              minHeight: itemHeight,
              cursor: 'pointer',
            }}
            onClick={() => handleOptionSelect(option)}
          >
            <ListItemText
              primary={highlightText(option.label, searchTerm)}
              primaryTypographyProps={{
                variant: 'body2',
                color: option.disabled ? 'text.disabled' : 'text.primary',
              }}
            />
            {isSelected && (
              <Chip
                label={t('tableDropdown.selected', 'Selected')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </ListItem>
        );
      },
      [value, highlightedIndex, itemClassName, itemHeight, handleOptionSelect, renderOption, t]
    );

    // Virtualized list item renderer
    const VirtualizedItem = useCallback(
      ({ index, style }: { index: number; style: React.CSSProperties }) => (
        <div style={style}>{renderOptionItem(filteredOptions[index], index)}</div>
      ),
      [filteredOptions, renderOptionItem]
    );

    // Handle scroll for infinite loading
    const handleScroll = useCallback(
      (props: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => {
        if (!hasMore || !onLoadMore || isLoadingMore) return;

        // For virtualized list, we need to calculate scroll percentage differently
        const totalHeight = filteredOptions.length * itemHeight;
        const scrollPercentage = (props.scrollOffset + maxHeight) / totalHeight;

        // Load more when user scrolls to 80% of the list
        if (scrollPercentage > 0.8) {
          throttledLoadMore();
        }
      },
      [hasMore, onLoadMore, filteredOptions.length, itemHeight, maxHeight, throttledLoadMore, isLoadingMore]
    );

    // Default input renderer
    const defaultInputRenderer = () => {
      if (selectedOption) {
        // Filled state - when a value is selected
        return (
          <Tooltip
            title={selectedOption.label}
            placement="top"
            slotProps={{ popper: sentryMaskIf(maskOptionLabels) }}
          >
            <TextField
              ref={inputRef}
              value={selectedOption.label}
              variant="standard"
              disabled={disabled}
              error={error}
              className={inputClassName}
              fullWidth
              autoComplete="off"
              data-dropdown-type-input={dataDropdownTypeInput}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              inputProps={{
                readOnly: true,
                role: 'combobox',
                'aria-expanded': isOpen,
                'aria-haspopup': 'listbox',
                'aria-autocomplete': 'none',
                ...sentryMaskIf(maskOptionLabels),
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LabelOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {loading && <CircularProgress size={20} />}
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onSelect(null);
                        setInputValue('');
                      }}
                      disabled={disabled}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                disableUnderline: true,
                style: COMMON_STYLE,
              }}
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
            />
          </Tooltip>
        );
      } else {
        // Unassigned state - when no value is selected
        return (
          <TextField
            ref={inputRef}
            placeholder={defaultPlaceholder}
            variant="standard"
            disabled={disabled}
            error={error}
            className={inputClassName}
            fullWidth
            autoComplete="off"
            data-dropdown-type-input={dataDropdownTypeInput}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            inputProps={{
              readOnly: true,
              role: 'combobox',
              'aria-expanded': isOpen,
              'aria-haspopup': 'listbox',
              'aria-autocomplete': 'none',
            }}
            sx={{ input: { '&::placeholder': { opacity: 1 } } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LabelOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading && <CircularProgress size={20} />}
                  <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleToggle();
                    }}
                    disabled={disabled}
                  >
                    <KeyboardArrowDownIcon />
                  </IconButton>
                </InputAdornment>
              ),
              disableUnderline: true,
              style: COMMON_STYLE,
            }}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
          />
        );
      }
    };

    return (
      <Box className={className} ref={anchorRef} data-dropdown-type={dataDropdownType}>
        {renderInput
          ? renderInput({
              ref: inputRef,
              value: inputValue,
              placeholder,
              label,
              disabled,
              error,
              helperText,
              className: inputClassName,
              'data-dropdown-type-input': dataDropdownTypeInput,
              'aria-expanded': isOpen,
              'aria-haspopup': 'listbox',
              onClick: handleToggle,
              onKeyDown: handleKeyDown,
              inputProps: {
                role: 'combobox',
                'aria-expanded': isOpen,
                'aria-haspopup': 'listbox',
                'aria-autocomplete': 'none',
              },
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    {loading && <CircularProgress size={20} />}
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (value) {
                          onSelect(null);
                          setInputValue('');
                        } else {
                          handleToggle();
                        }
                      }}
                      disabled={disabled}
                    >
                      {value ? <ClearIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            })
          : defaultInputRenderer()}

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className={dropdownClassName}
          style={{ zIndex: 1300 }}
          data-dropdown-id="table-dropdown"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <>
              {isOpen &&
                createPortal(
                  <Box
                    sx={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1299, // Just below the dropdown
                      backgroundColor: 'transparent',
                    }}
                    onClick={() => {
                      setIsOpen(false);
                      resetSearchState();
                      onClose?.();
                      onBlur?.();
                    }}
                  />,
                  document.body
                )}
              <Paper
                ref={dropdownContentRef}
                elevation={8}
                onClick={(e) => e.stopPropagation()}
                {...sentryMaskIf(maskOptionLabels)}
                sx={{
                  width: Math.max(
                    anchorRef.current?.offsetWidth || 320,
                    Math.min(450, window.innerWidth - 40)
                  ), // Reduced max width to 450px
                  maxHeight,
                  overflow: 'hidden',
                }}
                {...TransitionProps}
              >
                {searchable && (
                  <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <TextField
                      ref={searchInputRef}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder={defaultSearchPlaceholder}
                      variant="standard"
                      size="small"
                      fullWidth
                      autoComplete="off"
                      sx={{ input: { '&::placeholder': { opacity: 1 } } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        disableUnderline: true,
                        style: COMMON_STYLE,
                      }}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (searchInputRef.current) {
                          // Get the actual input element
                          const inputElement = searchInputRef.current.querySelector('input');
                          if (inputElement) {
                            inputElement.focus();
                            inputElement.select();
                          }
                        }
                      }}
                    />
                  </Box>
                )}

                {filteredOptions.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    {renderEmpty ? (
                      renderEmpty()
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {loading
                          ? t('tableDropdown.loadingOptions', 'Loading options...')
                          : searchTerm
                          ? t('tableDropdown.noOptionsFound', 'No options found')
                          : t('tableDropdown.noOptionsAvailable', 'No options available')}
                      </Typography>
                    )}
                  </Box>
                ) : virtualized ? (
                  <Box role="listbox" aria-label={t('tableDropdown.optionsList', 'Options list')}>
                    <VirtualizedList
                      ref={listRef}
                      height={Math.min(
                        maxHeight - (searchable ? 80 : 0),
                        filteredOptions.length * itemHeight
                      )}
                      itemCount={filteredOptions.length}
                      itemSize={itemHeight}
                      width="100%"
                      onScroll={handleScroll}
                    >
                      {VirtualizedItem}
                    </VirtualizedList>
                  </Box>
                ) : (
                  <List
                    sx={{ maxHeight: maxHeight - (searchable ? 80 : 0), overflow: 'auto' }}
                    role="listbox"
                    aria-label={t('tableDropdown.optionsList', 'Options list')}
                    onScroll={(event) => {
                      if (!hasMore || !onLoadMore || isLoadingMore) return;

                      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
                      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

                      // Load more when user scrolls to 80% of the list
                      if (scrollPercentage > 0.8) {
                        throttledLoadMore();
                      }
                    }}
                  >
                    {filteredOptions.map((option, index) => renderOptionItem(option, index))}
                  </List>
                )}

                {hasMore && onLoadMore && (
                  <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                        opacity: isLoadingMore ? 0.6 : 1,
                      }}
                      onClick={() => {
                        if (!isLoadingMore) {
                          onLoadMore();
                        }
                      }}
                    >
                      {isLoadingMore
                        ? t('tableDropdown.loading', 'Loading...')
                        : t('tableDropdown.loadMore', 'Load more...')}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Popper>
      </Box>
    );
  }
);

TableDropdown.displayName = 'TableDropdown';

// Custom comparison function for memo to optimize re-renders
const arePropsEqual = (prevProps: TableDropdownProps, nextProps: TableDropdownProps) => {
  // Compare primitive props
  if (
    prevProps.value !== nextProps.value ||
    prevProps.disabled !== nextProps.disabled ||
    prevProps.loading !== nextProps.loading ||
    prevProps.error !== nextProps.error ||
    prevProps.virtualized !== nextProps.virtualized ||
    prevProps.searchable !== nextProps.searchable ||
    prevProps.hasMore !== nextProps.hasMore ||
    prevProps.isLoadingMore !== nextProps.isLoadingMore ||
    prevProps.placeholder !== nextProps.placeholder ||
    prevProps.label !== nextProps.label ||
    prevProps.helperText !== nextProps.helperText ||
    prevProps.size !== nextProps.size ||
    prevProps.searchPlaceholder !== nextProps.searchPlaceholder ||
    prevProps.searchDebounceMs !== nextProps.searchDebounceMs ||
    prevProps.maxHeight !== nextProps.maxHeight ||
    prevProps.itemHeight !== nextProps.itemHeight ||
    prevProps.className !== nextProps.className ||
    prevProps.inputClassName !== nextProps.inputClassName ||
    prevProps.dropdownClassName !== nextProps.dropdownClassName ||
    prevProps.itemClassName !== nextProps.itemClassName ||
    prevProps['data-dropdown-type'] !== nextProps['data-dropdown-type'] ||
    prevProps['data-dropdown-type-input'] !== nextProps['data-dropdown-type-input'] ||
    prevProps.enableKeyboardNavigation !== nextProps.enableKeyboardNavigation ||
    prevProps.maskOptionLabels !== nextProps.maskOptionLabels
  ) {
    return false;
  }

  // If optionsVersion is provided, use it for quick comparison
  if (prevProps.optionsVersion !== undefined || nextProps.optionsVersion !== undefined) {
    if (prevProps.optionsVersion !== nextProps.optionsVersion) {
      return false;
    }
    // If versions match, we can skip the expensive options comparison
    return true;
  }

  // Compare options array length
  if (prevProps.options.length !== nextProps.options.length) {
    return false;
  }

  // If the options reference is the same, they're equal
  if (prevProps.options === nextProps.options) {
    return true;
  }

  // Deep compare all options
  for (let i = 0; i < prevProps.options.length; i++) {
    const prevOption = prevProps.options[i];
    const nextOption = nextProps.options[i];

    // Compare all relevant properties of each option
    if (
      prevOption.id !== nextOption.id ||
      prevOption.label !== nextOption.label ||
      prevOption.value !== nextOption.value ||
      prevOption.disabled !== nextOption.disabled
    ) {
      return false;
    }
  }

  return true;
};

export default memo(TableDropdown, arePropsEqual);
